/* eslint-disable complexity */
import React, {
    useReducer, useEffect, useCallback, useRef
} from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { Grid } from 'components/ui';
import ExpandableDeliveryOptions from 'components/ProductPage/DeliveryOptions/ExpandableDeliveryOptions';
import GetItShipped from 'components/ProductPage/DeliveryOptions/GetItShipped';
import BOPIS from 'components/ProductPage/DeliveryOptions/BOPIS';
import SameDayDelivery from 'components/ProductPage/DeliveryOptions/SameDayDelivery';
import AutoReplenishment from 'components/ProductPage/DeliveryOptions/AutoReplenishment';
import Actions from 'actions/Actions';
import deliveryOptionsUtils from 'utils/DeliveryOptions';
import basketConstants from 'constants/Basket';
import anaConsts from 'analytics/constants';
import bccUtils from 'utils/BCC';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import localeUtils from 'utils/LanguageLocale';
import helpersUtils from 'utils/Helpers';
import useDeliveryOptionsProps from 'components/ProductPage/DeliveryOptions/hooks/useDeliveryOptionsProps';

const isCanada = localeUtils.isCanada();
const { deferTaskExecution } = helpersUtils;
const {
    US_SHIPPING_INFO, ROPIS_INFO, CA_SHIPPING_INFO, BOPIS_INFO, SAME_DAY_DELIVERY_INFO, AUTO_REPLENISHMENT
} = bccUtils.MEDIA_IDS;
const { showStoreListModal } = deliveryOptionsUtils;
const { DELIVERY_OPTIONS, DELIVERY_OPTIONS_PARAMS } = basketConstants;

const initialState = {
    sameDayAvailable: true,
    renderSDULandingPage: false,
    isUserSDUTrialAllowed: false,
    selectedDeliveryOption: DELIVERY_OPTIONS.STANDARD,
    deliveryOptionLocked: false
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_SELECTED':
            return { ...state, selectedDeliveryOption: action.value };
        case 'TOGGLE_SDU_LANDING':
            return { ...state, renderSDULandingPage: !state.renderSDULandingPage };
        default:
            return state;
    }
};

const DeliveryOptions = ownProps => {
    const hookProps = useDeliveryOptionsProps(ownProps);
    // useDeliveryOptionsProps always computes its own SKU-eligibility-driven deliveryOptions
    // (used by PDP), which would otherwise silently clobber a caller-curated list — e.g. the
    // Choose Options Modal intentionally excludes Auto Replenish via its own deliveryOptions array.
    const props = {
        ...ownProps,
        ...hookProps,
        ...(ownProps.deliveryOptions && { deliveryOptions: ownProps.deliveryOptions })
    };
    const {
        currentProduct,
        deliveryOptions,
        reorderFulfillmentOptionsPdp,
        shouldSelectAutoReplenish,
        initialDeliveryOption,
        sddRadioButtonDisabled,
        showBopisSelectorCopyOnPdp,
        hasPickupMessage,
        signInText,
        forText,
        forFreeShippingText,
        showFastShippingCopySignedOut,
        freeShippingOnOrdersText,
        isFreeShippingCopyPDPActive,
        dynamicColumns = false,
        fromChooseOptionsModal,
        serviceUnavailable,
        sameDayNotAvailableForZip,
        sameDayAvailable,
        sameDayDeliveryClick,
        getItShippedClick,
        reserveAndPickupClick,
        autoReplenishClick,
        autoReplenishChecked,
        isAutoReplenMostCommon,
        replenishmentFreqNum,
        replenishmentFreqType,
        quantity,
        openDeliveryFrequencyModal,
        isSDUAddedToBasket,
        skuTrialEligibility,
        skuTrialPeriod,
        isUserSduTrialEligible,
        displayOrderCutoffCountdown,
        sameDayDelivery,
        shippingMethodNotAvailable,
        autoReplenProductEligibility,
        isReplenishmentEligible,
        showAutoReplenishment,
        resetGetItShipped,
        sduMediaId,
        triggerModalOpenAnalytics,
        reserveAndPickup
    } = props;

    const [state, localDispatch] = useReducer(reducer, {
        ...initialState,
        selectedDeliveryOption: props.selectedDeliveryOption || DELIVERY_OPTIONS.STANDARD
    });

    const dispatch = useDispatch();
    const isFirstDeliveryOptionsChangeRef = useRef(true);
    const isFirstProductChangeRef = useRef(true);
    const isFirstReorderChangeRef = useRef(true);
    const propsRef = useRef({ currentProduct, reserveAndPickup, fromChooseOptionsModal });
    propsRef.current = { currentProduct, reserveAndPickup, fromChooseOptionsModal };

    const boundShowStoreListModal = useCallback((e, options) => {
        showStoreListModal.call({ props: propsRef.current }, e, options);
    }, []);

    const showMediaModal = useCallback(opts => dispatch(Actions.showMediaModal(opts)), [dispatch]);
    const showSignInModal = useCallback(opts => dispatch(Actions.showSignInModal(opts)), [dispatch]);

    const fireAnalytics = useCallback((bccMediaSpec, isUserSDUTrialAllowed = false, isSDUInBasket = false) => {
        let pageDetail, linkData, pageType;

        if (bccMediaSpec.type === 'information') {
            pageDetail = anaConsts.PAGE_DETAIL.BCC_INFORMATION_MODAL;
            linkData = anaConsts.LinkData.VIEW_INFORMATION_ICON;
            pageType = anaConsts.PAGE_TYPES.ROPIS;

            if (bccMediaSpec.info === 'bopisStore') {
                pageType = anaConsts.PAGE_TYPES.BOPIS;
            }
        }

        if (bccMediaSpec.type === 'shipping') {
            pageDetail = anaConsts.PAGE_DETAIL.SHIPPING_INFORMATION + (isCanada ? ' (canada)' : ' (us)');
            linkData = anaConsts.LinkData.VIEW_SHIPPING_AND_RETURNS;
            pageType = anaConsts.PAGE_TYPES.CONTENT_STORE;
        }

        if (bccMediaSpec.type === 'shipping-estimate') {
            pageDetail = anaConsts.PAGE_DETAIL.ESTIMATED_SHIPPING_INFORMATION;
            pageType = anaConsts.PAGE_TYPES.SHIPPING;
        }

        if (bccMediaSpec.info === 'autoReplenish') {
            linkData = anaConsts.LinkData.AUTO_REPLENISH;
            pageType = anaConsts.PAGE_TYPES.AUTO_REPLENISH;
            pageDetail = anaConsts.PAGE_DETAIL.SUBSCRIPTION_INFO;
        }

        if (bccMediaSpec === 'SDULandingPage') {
            pageType = anaConsts.PAGE_NAMES.SAME_DAY_UNLIMITED;

            if (isUserSDUTrialAllowed) {
                pageDetail = isSDUInBasket ? anaConsts.PAGE_TYPES.TRIAL_ALREADY_ADDED : anaConsts.PAGE_TYPES.TRIAL_OFFER;
            } else {
                pageDetail = isSDUInBasket ? anaConsts.PAGE_TYPES.SUBSCRIPTION_ALREADY_ADDED : anaConsts.PAGE_TYPES.SUBSCRIPTION_OFFER;
            }
        }

        const data = { pageName: `${pageType}:${pageDetail}:n/a:*`, pageType, pageDetail, linkData };
        deferTaskExecution(() => pdpAnalytics.trackPageLoad(data));
    }, []);

    const showBCCMediaModal = useCallback(
        (event, bccMediaSpec = {}) => {
            event?.preventDefault();
            event?.stopPropagation();
            let mediaId, titleDataAt;

            if (bccMediaSpec.type === 'information') {
                mediaId = ROPIS_INFO;
                titleDataAt = 'informationModalTitle';

                if (bccMediaSpec.info === 'bopisStore') {
                    mediaId = BOPIS_INFO;
                }

                if (bccMediaSpec.info === 'sameDay') {
                    mediaId = SAME_DAY_DELIVERY_INFO;
                    titleDataAt = 'sameDayDeliveryModalTitle';
                }

                if (bccMediaSpec.info === 'autoReplenish') {
                    mediaId = AUTO_REPLENISHMENT;
                    titleDataAt = 'autoReplenishModalTitle';
                }
            }

            if (bccMediaSpec.type === 'shipping') {
                mediaId = isCanada ? CA_SHIPPING_INFO : US_SHIPPING_INFO;
                titleDataAt = 'shippingHandlingModalTitle';
            }

            showMediaModal({ isOpen: true, mediaId, titleDataAt });
            deferTaskExecution(() => fireAnalytics(bccMediaSpec));
        },
        [showMediaModal, fireAnalytics]
    );

    const onClickAnalytics = useCallback(
        (event, params = {}) => {
            event?.preventDefault();
            event?.stopPropagation();
            deferTaskExecution(() => fireAnalytics(params.bccSpecInfo));
        },
        [fireAnalytics]
    );

    const fireShippingOptionClickAnalytics = useCallback(
        shippingOptionText => {
            let actionInfo = anaConsts.EVENT_NAMES.FREE_SHIPPING_RADIO_BUTTON_CLICK;
            digitalData.page.attributes.outOfStock = '';

            if (shippingOptionText.toLowerCase() === anaConsts.DELIVERY_OPTIONS.Standard) {
                actionInfo = anaConsts.EVENT_NAMES.FREE_SHIPPING_RADIO_BUTTON_CLICK;

                if (currentProduct?.currentSku?.isOutOfStock) {
                    digitalData.page.attributes.outOfStock = `Yes - ${anaConsts.FULFILLMENT_TYPE.Standard}`;
                }
            }

            if (shippingOptionText.toLowerCase() === anaConsts.DELIVERY_OPTIONS.AutoReplenish) {
                actionInfo = anaConsts.EVENT_NAMES.AUTO_REPLENISH_RADIO_BUTTON_CLICK;

                if (currentProduct?.currentSku?.isOutOfStock) {
                    digitalData.page.attributes.outOfStock = `Yes - ${anaConsts.FULFILLMENT_TYPE.AutoReplenish}`;
                }
            }

            pdpAnalytics.trackLinkClick({
                actionInfo,
                prop44: digitalData.page.attributes.outOfStock,
                productStrings: `;${currentProduct?.currentSku.skuId};;;;eVar26=${currentProduct?.currentSku.skuId}`
            });
        },
        [currentProduct]
    );

    const setSelectedDeliveryOption = useCallback(
        (selectedDeliveryOption, { trackAnalytics = true } = {}) => {
            localDispatch({ type: 'SET_SELECTED', value: selectedDeliveryOption });

            deferTaskExecution(() => {
                autoReplenishClick(false);

                switch (selectedDeliveryOption) {
                    case DELIVERY_OPTIONS.PICKUP:
                        reserveAndPickupClick();

                        break;
                    case DELIVERY_OPTIONS.STANDARD:
                        getItShippedClick();

                        if (trackAnalytics) {
                            fireShippingOptionClickAnalytics(selectedDeliveryOption);
                        }

                        break;
                    case DELIVERY_OPTIONS.AUTO_REPLENISH:
                        getItShippedClick();
                        autoReplenishClick(true);

                        if (trackAnalytics) {
                            fireShippingOptionClickAnalytics(selectedDeliveryOption);
                        }

                        break;
                    case DELIVERY_OPTIONS.SAME_DAY:
                        sameDayDeliveryClick(state.sameDayAvailable);

                        break;
                    default:
                        break;
                }
            });
        },
        [autoReplenishClick, reserveAndPickupClick, getItShippedClick, sameDayDeliveryClick, fireShippingOptionClickAnalytics, state.sameDayAvailable]
    );

    const shouldAutoReplenBeSelected = useCallback(() => {
        if (state.selectedDeliveryOption === DELIVERY_OPTIONS.AUTO_REPLENISH) {
            const isAutoReplenishDisabled = deliveryOptions.find(opt =>
                Boolean(opt.deliveryOption === DELIVERY_OPTIONS.AUTO_REPLENISH && opt.disabled)
            );

            if (isAutoReplenishDisabled) {
                resetGetItShipped(() => setSelectedDeliveryOption(DELIVERY_OPTIONS.STANDARD, { trackAnalytics: false }));
            }
        }
    }, [state.selectedDeliveryOption, deliveryOptions, resetGetItShipped, setSelectedDeliveryOption]);

    const toggleSDULandingPage = useCallback(() => {
        if (fromChooseOptionsModal) {
            deliveryOptionsUtils.showSDULandingPageModal({
                isOpen: true,
                mediaId: sduMediaId,
                skuTrialPeriod,
                isSDUAddedToBasket,
                isUserSDUTrialEligible: isUserSduTrialEligible,
                isCanada: localeUtils.isCanada(),
                skipConfirmationModal: true,
                isUserSDUTrialAllowed: state.isUserSDUTrialAllowed,
                fromChooseOptionsModal
            });
        } else {
            localDispatch({ type: 'TOGGLE_SDU_LANDING' });

            if (!state.renderSDULandingPage) {
                fireAnalytics('SDULandingPage', state.isUserSDUTrialAllowed, isSDUAddedToBasket);
                triggerModalOpenAnalytics && triggerModalOpenAnalytics(isSDUAddedToBasket);
            }
        }
    }, [
        fromChooseOptionsModal,
        sduMediaId,
        skuTrialPeriod,
        isSDUAddedToBasket,
        isUserSduTrialEligible,
        state,
        fireAnalytics,
        triggerModalOpenAnalytics
    ]);

    useEffect(() => {
        if (shouldSelectAutoReplenish && state.selectedDeliveryOption !== DELIVERY_OPTIONS.AUTO_REPLENISH) {
            setSelectedDeliveryOption(DELIVERY_OPTIONS.AUTO_REPLENISH, { trackAnalytics: false });
        }

        if (initialDeliveryOption && state.selectedDeliveryOption !== initialDeliveryOption) {
            const deliveryOption = DELIVERY_OPTIONS_PARAMS[initialDeliveryOption];

            if (deliveryOption) {
                setSelectedDeliveryOption(deliveryOption, { trackAnalytics: false });
            }
        }

        if (reorderFulfillmentOptionsPdp && !deliveryOptions?.[0]?.disabled) {
            setSelectedDeliveryOption(DELIVERY_OPTIONS.PICKUP, { trackAnalytics: false });
        }
    }, []);

    useEffect(() => {
        if (isFirstDeliveryOptionsChangeRef.current) {
            isFirstDeliveryOptionsChangeRef.current = false;

            return;
        }

        if (state.deliveryOptionLocked) {
            return;
        }

        const autoReplenishIndex = reorderFulfillmentOptionsPdp ? 2 : 1;
        const arDisabled = deliveryOptions?.[autoReplenishIndex]?.disabled;

        if (arDisabled !== undefined) {
            shouldAutoReplenBeSelected();
        }
    }, [deliveryOptions?.map(o => o.disabled).join(',')]);

    useEffect(() => {
        if (isFirstProductChangeRef.current) {
            isFirstProductChangeRef.current = false;

            return;
        }

        if (state.deliveryOptionLocked) {
            return;
        }

        const shouldSelectBopis = !deliveryOptions?.[0]?.disabled && reorderFulfillmentOptionsPdp;
        setSelectedDeliveryOption(shouldSelectBopis ? DELIVERY_OPTIONS.PICKUP : DELIVERY_OPTIONS.STANDARD, { trackAnalytics: false });
    }, [currentProduct?.productDetails?.productId]);

    useEffect(() => {
        if (isFirstReorderChangeRef.current) {
            isFirstReorderChangeRef.current = false;

            return;
        }

        if (state.deliveryOptionLocked) {
            return;
        }

        if (reorderFulfillmentOptionsPdp && !deliveryOptions?.[0]?.disabled) {
            setSelectedDeliveryOption(DELIVERY_OPTIONS.PICKUP, { trackAnalytics: false });
        }
    }, [reorderFulfillmentOptionsPdp]);

    const renderDeliveryOption = useCallback(
        (option, isSelected) => {
            switch (option.deliveryOption) {
                case DELIVERY_OPTIONS.STANDARD:
                    return (
                        <GetItShipped
                            currentProduct={currentProduct}
                            currentSku={currentProduct?.currentSku}
                            shippingMethodNotAvailable={shippingMethodNotAvailable}
                            serviceUnavailable={serviceUnavailable}
                            sameDayNotAvailableForZip={sameDayNotAvailableForZip}
                            autoReplenProductEligibility={autoReplenProductEligibility}
                            isReplenishmentEligible={isReplenishmentEligible}
                            showAutoReplenishment={showAutoReplenishment}
                            quantity={quantity}
                            isUserSduTrialEligible={isUserSduTrialEligible}
                            showBCCMediaModal={showBCCMediaModal}
                            onClickAnalytics={onClickAnalytics}
                            sameDayDeliveryClick={getItShippedClick}
                            fromChooseOptionsModal={fromChooseOptionsModal}
                        />
                    );
                case DELIVERY_OPTIONS.SAME_DAY:
                    return (
                        <SameDayDelivery
                            currentProduct={currentProduct}
                            sameDayNotAvailableForZip={sameDayNotAvailableForZip}
                            sameDayAvailable={sameDayAvailable}
                            sameDayDelivery={sameDayDelivery}
                            sameDayDeliveryClick={sameDayDeliveryClick}
                            serviceUnavailable={serviceUnavailable}
                            showBCCMediaModal={showBCCMediaModal}
                            bccMediaSpecInfo={option.bccMediaSpecInfo}
                            displayOrderCutoffCountdown={displayOrderCutoffCountdown}
                            isSDUAddedToBasket={isSDUAddedToBasket}
                            skuTrialEligibility={skuTrialEligibility}
                            skuTrialPeriod={skuTrialPeriod}
                            toggleSDULandingPage={toggleSDULandingPage}
                            renderSDULandingPage={state.renderSDULandingPage}
                            isUserSDUTrialEligible={isUserSduTrialEligible}
                            fromChooseOptionsModal={fromChooseOptionsModal}
                        />
                    );
                case DELIVERY_OPTIONS.PICKUP:
                    return (
                        <BOPIS
                            currentProduct={fromChooseOptionsModal ? currentProduct : option.currentProduct}
                            preferredStoreInfo={option.preferredStoreInfo}
                            reserveAndPickupClick={reserveAndPickupClick}
                            showStoreListModal={boundShowStoreListModal}
                            disabledText={option.disabled && option.subTitle}
                            displayOrderCutoffCountdown={displayOrderCutoffCountdown}
                            showBCCMediaModal={showBCCMediaModal}
                            bccMediaSpecInfo={option.bccMediaSpecInfo}
                            isSelected={isSelected}
                            isOutOfStockWithNearbyInStockAlternative={option.isOutOfStockWithNearbyInStockAlternative}
                            onClickAnalytics={onClickAnalytics}
                        />
                    );
                case DELIVERY_OPTIONS.AUTO_REPLENISH:
                    return (
                        <AutoReplenishment
                            currentProduct={currentProduct}
                            currentSku={currentProduct?.currentSku}
                            autoReplenProductEligibility={autoReplenProductEligibility}
                            isReplenishmentEligible={isReplenishmentEligible}
                            showAutoReplenishment={showAutoReplenishment}
                            quantity={quantity}
                            showBCCMediaModal={showBCCMediaModal}
                            replenishmentFreqNum={replenishmentFreqNum}
                            autoReplenishChecked={autoReplenishChecked}
                            isAutoReplenMostCommon={isAutoReplenMostCommon}
                            replenishmentFreqType={replenishmentFreqType}
                            openDeliveryFrequencyModal={openDeliveryFrequencyModal}
                            bccMediaSpecInfo={option.bccMediaSpecInfo}
                            showAutoReplenishmentDetails={false}
                        />
                    );
                default:
                    return null;
            }
        },
        [props, state.renderSDULandingPage, showBCCMediaModal, onClickAnalytics, toggleSDULandingPage, boundShowStoreListModal]
    );

    const handleChangeDeliveryOption = useCallback(
        option => () => {
            deferTaskExecution(() => {
                if (!option?.disabled) {
                    setSelectedDeliveryOption(option.deliveryOption);
                }
            });
        },
        [setSelectedDeliveryOption]
    );

    const { selectedDeliveryOption } = state;

    if (typeof digitalData === 'object' && digitalData && digitalData?.page?.attributes) {
        digitalData.page.attributes.isSameDayDeliveryAvailable = !sddRadioButtonDisabled;
    }

    return (
        <Grid
            is='fieldset'
            gap={2}
            lineHeight='tight'
            {...{
                columns: [null, dynamicColumns ? deliveryOptions.filter(option => !option.hideDeliveryOption).length : 4],
                marginTop: 4,
                marginBottom: 4
            }}
        >
            {deliveryOptions.map((option, index) => {
                const isSelected = selectedDeliveryOption === option.deliveryOption && !option.disabled;
                const showAlternativeSubTitle = Boolean(option.alternativeSubTitle) && isSelected;
                const subHeader = showAlternativeSubTitle ? option.alternativeSubTitle : option.subTitle;
                const desktopSubHeader = showAlternativeSubTitle ? option.alternativeSubTitle : option.desktopSubTitle;

                return (
                    option.hideDeliveryOption || (
                        <ExpandableDeliveryOptions
                            id={`delivery_option_${index}`}
                            key={`delivery_option_${index}`}
                            header={option.title}
                            smHeader={option.smTitle}
                            subHeader={subHeader}
                            smSubHeader={option.smSubTitle}
                            desktopSubHeader={desktopSubHeader}
                            enableMarkdownSubtitle={option.enableMarkdownSubtitle}
                            iconName={option.iconName}
                            deliveryOption={option.deliveryOption}
                            handleChange={handleChangeDeliveryOption(option)}
                            selected={isSelected}
                            disabled={option.disabled}
                            showBCCMediaModal={showBCCMediaModal}
                            bccMediaSpecInfo={option.bccMediaSpecInfo}
                            showBopisSelectorCopyOnPdp={showBopisSelectorCopyOnPdp}
                            hasPickupMessage={hasPickupMessage}
                            applyGreen={option.applyGreen}
                            signInText={signInText}
                            forText={forText}
                            forFreeShippingText={forFreeShippingText}
                            showFastShippingCopySignedOut={showFastShippingCopySignedOut}
                            freeShippingOnOrdersText={freeShippingOnOrdersText}
                            isFreeShippingCopyPDPActive={isFreeShippingCopyPDPActive}
                            showSignInModal={showSignInModal}
                            fromChooseOptionsModal={fromChooseOptionsModal}
                        >
                            {renderDeliveryOption(option, isSelected)}
                        </ExpandableDeliveryOptions>
                    )
                );
            })}
        </Grid>
    );
};

export default wrapFunctionalComponent(DeliveryOptions, 'DeliveryOptions');
